import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Clock, Download, Printer, ArrowRight, AlertTriangle } from '../icons';
import { getContractForSigning, signContract, contractDocumentUrl } from '../services/api';
import SignaturePad from '../components/SignaturePad';

// Points at the one thing the signer needs to do next, the way e-signing tools do.
const Cue = ({ children }) => (
  <p className="sc-cue"><ArrowRight size={14} /> <span>{children}</span></p>
);

const fmtStamp = (d) => (d ? new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const fmtLong = (d) => {
  if (!d) return '';
  const date = typeof d === 'string' && d.length === 10 ? new Date(`${d}T00:00:00`) : new Date(d);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

function ContractSign() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signature, setSignature] = useState('');
  const [mode, setMode] = useState('type'); // 'type' | 'draw'
  const [drawn, setDrawn] = useState(null);
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Red only once they have tried to sign — an untouched form should guide, not scold.
  const [attempted, setAttempted] = useState(false);
  const nameRef = useRef(null);
  const padRef = useRef(null);
  const agreeRef = useRef(null);

  useEffect(() => {
    getContractForSigning(token)
      .then((res) => setContract(res.data))
      .catch((err) => setError(err.response?.status === 404
        ? 'This contract link is not valid.'
        : 'We could not load this contract. Please try again later.'))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setAttempted(true);
    // The button stays enabled so a signer who misses a step is told which one,
    // instead of facing a dead button with no explanation.
    if (missing) {
      const target = { name: nameRef, draw: padRef, agree: agreeRef }[missing]?.current;
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (missing === 'name') nameRef.current?.focus({ preventScroll: true });
      return;
    }
    setSubmitError('');
    setSubmitting(true);
    try {
      const res = await signContract(token, { signature, agree, signature_image: mode === 'draw' ? drawn : undefined });
      setContract(res.data);
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Could not record your signature. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="sc-sign-page"><p className="sc-sign-note">Loading…</p></div>;
  if (error) return <div className="sc-sign-page"><p className="sc-sign-note">{error}</p></div>;

  const { status } = contract;
  const youSigned = !!contract.signer_signed_at;
  const roster = contract.signers || [];
  const everyoneSigned = roster.length > 0 && roster.every((s) => s.signed);
  const canSign = status === 'awaiting_signature' && !youSigned;
  // The frozen copy only exists once everyone has signed.
  const hasCopy = status === 'completed' || status === 'countersign';

  // What is still outstanding, in the order the signer works through it.
  const wrong = {
    name: signature.trim().length < 2,
    draw: mode === 'draw' && !drawn,
    agree: !agree,
  };
  const missing = ['name', 'draw', 'agree'].find((k) => wrong[k]) || null;
  // Before they try: amber arrow on the current step. After: red on what is missing.
  const nextStep = attempted ? null : missing;
  const isWrong = (k) => attempted && wrong[k];
  const fieldClass = (k) => (isWrong(k) ? 'sc-invalid' : nextStep === k ? 'sc-todo' : '');

  return (
    <div className="sc-sign-page">
      <div className="sc-sign-brand">{contract.studio_name}</div>
      <div className="sc-sign-head">
        <h1>{contract.name}</h1>
        <p>From {contract.studio_name}{contract.project_name ? ` · ${contract.project_name}` : ''}</p>
        {contract.expires_at && status === 'awaiting_signature' && (
          <p className="sc-sign-expiry"><Clock size={14} /> Please sign by {fmtLong(contract.expires_at)}</p>
        )}
      </div>

      {status === 'canceled' && <div className="sc-sign-banner sc-sign-warn">This contract has been canceled and can no longer be signed.</div>}
      {youSigned && !everyoneSigned && status !== 'completed' && (
        <div className="sc-sign-banner sc-sign-ok"><CheckCircle size={18} /> Thank you, {contract.signer_name}. You signed on {fmtStamp(contract.signer_signed_at)}. We're waiting on the other signer(s).</div>
      )}
      {status === 'countersign' && everyoneSigned && (
        <div className="sc-sign-banner sc-sign-ok"><CheckCircle size={18} /> All signers have signed. {contract.studio_name} will countersign next.</div>
      )}
      {status === 'completed' && (
        <div className="sc-sign-done">
          <h2><CheckCircle size={20} /> Congratulations — we're all set!</h2>
          <p>
            This contract is fully signed by all parties. {contract.studio_name} is excited to work with you.
            A copy has also been emailed to you — keep it somewhere safe for your records.
          </p>
        </div>
      )}

      {hasCopy && (
        <div className="sc-sign-actions">
          {/* download: save the file instead of navigating away from this page */}
          <a className="btn btn-primary" href={contractDocumentUrl(token, 'pdf')} download={`${contract.name} - signed.pdf`}>
            <Download size={16} /> Download PDF
          </a>
          <a className="btn btn-outline" href={contractDocumentUrl(token, 'html')} download={`${contract.name} - signed.html`}>
            <Download size={16} /> Download HTML
          </a>
          <button type="button" className="btn btn-outline" onClick={() => window.print()}>
            <Printer size={16} /> Print / Save as PDF
          </button>
        </div>
      )}

      {roster.length > 1 && (
        <div className="sc-sign-roster">
          {roster.map((s, i) => (
            <span key={i} className={`sc-roster-chip ${s.signed ? 'done' : ''}`}>
              {s.signed ? <CheckCircle size={13} /> : <Clock size={13} />} {s.name}
            </span>
          ))}
        </div>
      )}

      <div className="sc-paper sc-sign-paper">
        <div className="sc-doc" dangerouslySetInnerHTML={{ __html: contract.content }} />

        {(roster.some((s) => s.signed) || contract.studio_signature) && (
          <div className="sc-sigblock">
            {roster.map((s, i) => (
              <div key={i}>
                {s.signature_image
                  ? <div className="sc-sig sc-sig-drawn"><img src={s.signature_image} alt={`Signature of ${s.name}`} /></div>
                  : <span className="sc-sig">{s.signature || '—'}</span>}
                <small>{s.name}{s.signed_at ? ` · ${fmtStamp(s.signed_at)}` : ' · awaiting signature'}</small>
              </div>
            ))}
            <div>
              <span className="sc-sig">{contract.studio_signature || 'Awaiting countersignature'}</span>
              <small>{contract.studio_name}{contract.studio_signed_at ? ` · ${fmtStamp(contract.studio_signed_at)}` : ''}</small>
            </div>
          </div>
        )}
      </div>

      {canSign && (
        // noValidate: our own messages guide the signer, not the browser's bubbles
        <form className="sc-sign-form" onSubmit={submit} noValidate>
          <h3>Sign this contract</h3>

          {nextStep === 'name' && <Cue>Start here — type your full name</Cue>}
          <label className="sc-field">
            <span>Type your full name</span>
            <input
              ref={nameRef}
              className={fieldClass('name')}
              maxLength={200}
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              aria-invalid={isWrong('name') || undefined}
              aria-describedby={isWrong('name') ? 'sc-err-name' : undefined}
            />
          </label>
          {isWrong('name') && (
            <p className="sc-fielderr" id="sc-err-name"><AlertTriangle size={14} /> Please type your full name before signing.</p>
          )}

          <div className="sc-sigmodes" role="tablist" aria-label="How to sign">
            <button type="button" role="tab" aria-selected={mode === 'type'} className={mode === 'type' ? 'active' : ''} onClick={() => setMode('type')}>Type</button>
            <button type="button" role="tab" aria-selected={mode === 'draw'} className={mode === 'draw' ? 'active' : ''} onClick={() => setMode('draw')}>Draw</button>
          </div>

          {mode === 'type' ? (
            signature.trim() && <div className="sc-sig-preview">{signature}</div>
          ) : (
            <>
              {nextStep === 'draw' && <Cue>Now draw your signature below</Cue>}
              <div ref={padRef} className={`sc-padwrap ${fieldClass('draw')}`}>
                <SignaturePad onChange={setDrawn} disabled={submitting} />
              </div>
              {isWrong('draw') && (
                <p className="sc-fielderr"><AlertTriangle size={14} /> Please draw your signature, or switch to Type.</p>
              )}
            </>
          )}

          {nextStep === 'agree' && <Cue>Last step — tick the box to agree</Cue>}
          <label className={`sc-check ${fieldClass('agree')}`} ref={agreeRef}>
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              aria-invalid={isWrong('agree') || undefined}
            />
            <span>I have read this contract and agree to sign it electronically. I understand this is legally binding.</span>
          </label>
          {isWrong('agree') && (
            <p className="sc-fielderr"><AlertTriangle size={14} /> Please tick the box to confirm you agree.</p>
          )}

          {submitError && <p className="sc-sign-error">{submitError}</p>}
          <button className="btn btn-primary" type="submit" disabled={submitting}>
            {submitting ? 'Signing…' : 'Sign Contract'}
          </button>
        </form>
      )}

      <p className="sc-sign-foot">
        This contract was sent to you by {contract.studio_name}. If you have questions about it, reply to the email you received.
      </p>
    </div>
  );
}

export default ContractSign;
